/** @format */
import React, { useMemo, useState } from 'react';

import { SearchFacet } from '@toucan/common/services/search/constants';

import {
    Accordion,
    AccordionSection,
} from '@toucan/client/blocks/components/accordion';

import {
    Filter,
    FilterOption,
} from '@toucan/client/features/search/components/model-search-results/types';

import { SearchFilter } from '../search-filter';
import { SectionTitleChildNode } from '../section-title';

export const FilterAccordion = ({
    'data-testid': dataTestId,
    onFilterOpen,
    selectedFilters,
    handleFilterClear,
    handleFilterUpdate,
    filters,
    isLoadingFilters,
}: {
    'data-testid': string;
    onFilterOpen?: (id: SearchFacet) => Promise<void> | void;
    selectedFilters: Record<SearchFacet, Filter>;
    handleFilterClear: (filterId: SearchFacet) => void;
    handleFilterUpdate: (filter: Filter, items: FilterOption[]) => void;
    filters: Filter[];
    isLoadingFilters: boolean;
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeAccordionSection, setActiveAccordionSection] = useState<
        string | null
    >(null);

    async function handleActiveSectionChange(section: string | null) {
        setIsLoading(true);
        setActiveAccordionSection(section);

        if (section) {
            await onFilterOpen?.(section as SearchFacet);
        }

        setIsLoading(false);
    }

    const accordionSections: AccordionSection[] = useMemo(() => {
        return filters.map((filter) => {
            const { label, id } = filter;

            const selectedItems = selectedFilters?.[id]?.values ?? [];

            return {
                id,
                title: label,
                showTitleChildNode: true,
                titleChildNode: (
                    <SectionTitleChildNode
                        filterId={id}
                        isOpen={activeAccordionSection === id}
                        selectedCount={selectedItems.length}
                        onClearAll={handleFilterClear}
                    />
                ),
                content: (
                    <SearchFilter
                        data-testid={`${dataTestId}__search-filter`}
                        filter={filter}
                        selectedItems={selectedItems}
                        isLoading={isLoading || isLoadingFilters}
                        onUpdate={handleFilterUpdate}
                    />
                ),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [filters, selectedFilters, activeAccordionSection, isLoadingFilters]);

    return (
        <Accordion
            data-testid={dataTestId}
            className="filter-accordion"
            activeSection={activeAccordionSection}
            sections={accordionSections}
            onActiveSectionChange={handleActiveSectionChange}
        />
    );
};
